<template>
  <email-verification
    v-if="showEmailVerification"
    :email="email"
    :id="verifyId"
    @verified="submit"
    ref="mfa"
  ></email-verification>
  <div v-else-if="showMFAPrompt">
    <p>{{ $t("message.multi-factor-prompt") }}</p>
    <code-input
      v-model="mfaCode"
      @code="submit"
      :readonly="submitting"
    ></code-input>

    <div class="errors" v-if="errors">
      <span class="error" v-html="errors"></span>
    </div>

    <div class="buttons">
      <button class="button" type="button" @click="reset">
        {{ $t("message.cancel") }}
      </button>
    </div>
  </div>
  <div class="signin" v-else>
    <form name="signin-form" @submit.prevent="submit">
      <label class="form-label" for="email">{{ $t("message.email") }}</label>
      <input
        class="form-input"
        type="email"
        v-model="email"
        :placeholder="$t('message.email')"
        name="email"
        @blur="handleEmailBlur"
        :readonly="changingPassword"
        :class="{ disabled: changingPassword }"
        autocomplete="email"
        required
      />
      <label class="form-label" for="password">{{
        $t("message.password")
      }}</label>
      <input
        class="form-input"
        type="password"
        v-model="password"
        :placeholder="$t('message.password')"
        name="password"
        :readonly="changingPassword"
        :class="{ disabled: changingPassword }"
        autocomplete="current-password"
        required
      />

      <password-field
        v-model="newPassword"
        v-if="changingPassword"
      ></password-field>

      <div class="errors" v-if="errors">
        <span class="error" v-html="errors"></span>
      </div>

      <div class="buttons">
        <button
          name="signin-button"
          class="button primary-bg secondary-bg-hover primary-border"
          type="submit"
        >
          {{ signinButtonLabel }}
        </button>
        <button
          class="button"
          type="button"
          v-if="changingPassword"
          @click="reset"
        >
          {{ $t("message.cancel") }}
        </button>
      </div>
    </form>

    <p>
      <router-link
        :to="{ name: 'forgot-password' }"
        class="text-link primary-text-hover"
        >{{ $t("message.forgot-password-link") }}</router-link
      >
    </p>
  </div>
</template>

<style type="less" scoped>
.form-input.disabled {
  color: #666;
  background-color: #efefef;
}
</style>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import CheckEmailMixin from "./CheckEmailMixin";
import EmailVerification from "./EmailVerification";
import CodeInput from "./CodeInput";
import PasswordField from "./PasswordField";

export default {
  name: "Signin",
  components: { CodeInput, EmailVerification, PasswordField },
  mixins: [CheckEmailMixin],
  data() {
    return {
      email: "",
      password: "",
      errors: null,
      changingPassword: false,
      newPassword: "",
      confirmPassword: "",
      showEmailVerification: false,
      verifyId: null,
      showMFAPrompt: false,
      mfaCode: "",
    };
  },
  computed: {
    signinButtonLabel() {
      return this.edition && this.edition.saml_provider
        ? this.$t("message.sign-in-with-email")
        : this.$t("message.sign-in");
    },
    ...mapGetters(["currentEdition"]),
  },
  methods: {
    async submit() {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      this.errors = null;
      let data = {
        email: this.email,
        password: this.password,
        redir: sessionStorage.redir,
      };
      if (this.changingPassword) {
        data.new_password = this.newPassword;
      }
      if (this.mfaCode) {
        data.totp_code = this.mfaCode;
      }
      try {
        let res = await axios.post("/signin/process_signin", data);

        // record login event
        try {
          if (window.dataLayer) {
            dataLayer.push({
              event: "login",
              loginMethod: "email",
            });
          }
        } catch (e) {}

        let resolved = this.$router.resolve(res.data.destination);
        if (resolved.route.name) {
          this.$router.push(resolved.location);
        } else {
          window.location.href = res.data.destination;
        }
      } catch (error) {
        let message = this.$t("message.unknown-signin-error");
        if (error.response && error.response.data.error) {
          switch (error.response.data.error.type) {
            case "SigninCredentialError":
              message = this.$t("message.invalid-credentials");
              break;
            case "RequirePasswordChange":
              message = this.$t("message.require-password-change");
              this.showEmailVerification = false;
              this.changingPassword = true;
              break;
            case "EmailNotVerifiedError":
              message = this.$t("message.require-password-change");
              this.showEmailVerification = true;
              this.verifyId = error.response.data.error.verify_id;
              break;
            case "RequireMultiFactor":
              message = null; // Don't show an error on first request for code
              this.showMFAPrompt = true;
              break;
            case "InvalidMultiFactor":
              message = this.$t("message.invalid-multi-factor");
              this.mfaCode = "";
              break;
            default:
              message = error.response.data.error.description;
              if (!error.response.data.error.html_safe) {
                // should be safe as comes from api, but remove html in case
                let decoder = document.createElement("div");
                decoder.innerHTML = message;
                message = decoder.textContent;
              }
          }
        }
        this.errors = message;
      }
      this.submitting = false;
    },
    reset() {
      this.changingPassword = false;
      this.showMFAPrompt = false;
      this.email = "";
      this.password = "";
      this.mfaCode = "";
      this.errors = null;
    },
    handleEmailBlur() {
      if (!this.changingPassword) {
        this.checkEmail();
      }
    },
  },
};
</script>
